import { z } from 'zod';

import type {
  Order,
  PostAdminV1StoresStoreIdOrdersIdReturnsBody,
} from '@nucleus-commerce/api-client/admin';

import { returnableLines } from './return-rules';

const whole = z
  .string()
  .trim()
  .refine((v) => /^\d+$/.test(v), { error: 'orders:returns.validation.wholeNumber' });

/** Caps each line at the shipped units no other return holds, the way the backend caps it. */
export function openReturnSchema(order: Pick<Order, 'lines' | 'returns'>) {
  const caps = new Map(returnableLines(order).map(({ line, max }) => [line.id, max]));
  return z
    .object({
      lines: z.array(z.object({ orderLineId: z.string(), quantity: whole })),
      reason: z
        .string()
        .trim()
        .min(1, { error: 'orders:returns.validation.reasonRequired' })
        .max(500, { error: 'orders:returns.validation.reasonLength' }),
      note: z.string().trim().max(2000, { error: 'orders:returns.validation.noteLength' }),
      resize: z.boolean(),
      staffNote: z.string().trim().max(2000, { error: 'orders:returns.validation.noteLength' }),
      returnShippingPaidBy: z.enum(['default', 'customer', 'store']),
    })
    .superRefine((v, ctx) => {
      v.lines.forEach((line, index) => {
        if (Number(line.quantity) > (caps.get(line.orderLineId) ?? 0)) {
          ctx.addIssue({
            code: 'custom',
            path: ['lines', index, 'quantity'],
            message: 'orders:returns.validation.overQuantity',
          });
        }
      });
      if (!v.lines.some((line) => Number(line.quantity) > 0)) {
        ctx.addIssue({
          code: 'custom',
          path: ['lines'],
          message: 'orders:returns.validation.noLines',
        });
      }
    });
}

export type OpenReturnInput = z.input<ReturnType<typeof openReturnSchema>>;
export type OpenReturnOutput = z.output<ReturnType<typeof openReturnSchema>>;

export function emptyOpenReturnForm(order: Pick<Order, 'lines' | 'returns'>): OpenReturnInput {
  return {
    lines: returnableLines(order).map(({ line }) => ({ orderLineId: line.id, quantity: '0' })),
    reason: '',
    note: '',
    resize: false,
    staffNote: '',
    returnShippingPaidBy: 'default',
  };
}

/** "default" leaves the choice to the store's `settings.returns.returnShippingPaidBy`. */
export function toOpenReturnBody(
  values: OpenReturnOutput,
): PostAdminV1StoresStoreIdOrdersIdReturnsBody {
  return {
    lines: values.lines
      .map((line) => ({ orderLineId: line.orderLineId, quantity: Number(line.quantity) }))
      .filter((line) => line.quantity > 0),
    reason: values.reason,
    resize: values.resize,
    ...(values.note ? { note: values.note } : {}),
    ...(values.staffNote ? { staffNote: values.staffNote } : {}),
    ...(values.returnShippingPaidBy !== 'default'
      ? { returnShippingPaidBy: values.returnShippingPaidBy }
      : {}),
  };
}
